import { LitElement, css } from "lit-element";
import { classMap } from "lit-html/directives/class-map";
import { html } from "lit-html";
import { storeInstance } from "store/index.js";
import range from "../utils/range";
import "@vaadin/vaadin-icons/vaadin-icons.js";
import "bds-tree";
import "./bds-property-filter";

/**
 * Shared Functionality Between Search Pages
 * @property {Array} controlDefs describes the type, id and other metadata about control
 * @property {Array} controlData contains user visible data and other values
 * @property {Object} propertyFilterState used to set the initial state for the property filter
 * @property {Number} partitionSize number of page numbers viewable
 * @property {Boolean} hasMorePages show right arrow if there are more results
 */

export class KbSearchBase extends LitElement {
  static get properties() {
    return {
      hasPaging: { type: Boolean },
      currentPage: { type: Number },
      pageNumbers: { type: Array },
      partitionSize: { type: Number },
      resultsPerPage: { type: Number },
      hasMorePages: { type: Boolean },
      isLoading: { type: Boolean },
      controlDefs: { type: Array },
      controlData: { type: Array },
      propertyFilterState: { type: Object },
      resultsClasses: { type: Object },
      searchResults: { type: Array },
      visibleResults: { type: Array },
      pageNumberClasses: { type: Object },
    };
  }

  pageName = "kbsearch";

  constructor() {
    super();
    this.pageNumbers = [1];
    this.partitionSize = 1;
    this.visibleResults = [];
    this.hasMorePages = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this.subscription = storeInstance.subscribe((state) => {
      this.stateChange(state);
    });
    this.state = storeInstance.state;
    this.getDataFromStore();
    this.resultsClasses = {
      results: this.controlDefs.length > 0,
      "results--empty": this.controlDefs.length == 0,
    };
    window.addEventListener(
      "popstate",
      this.handleHistoryButtonClick.bind(this)
    );
    window.addEventListener(
      "vaadin-router-location-changed",
      this.resetSearchState.bind(this)
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    storeInstance.unsubscribe(this.subscription);
    window.removeEventListener("popstate", this.handleHistoryButtonClick);
    window.removeEventListener(
      "vaadin-router-location-changed",
      this.resetSearchState
    );
  }

  stateChange(state) {
    this.state = state;
    this.getDataFromStore();
    this.resultsClasses = {
      results: this.controlDefs.length > 0,
      "results--empty": this.controlDefs.length == 0,
    };
  }

  getDataFromStore() {
    this.string = this.state.strings;
    this.controlDefs = this.state.panel_control_defs;
    this.controlData = this.state.control_def_data_array;
    this.isLoading = this.state.is_results_loading;
    this.currentPage = this.state.current_results_page;
    this.resultsPerPage = this.state.max_results;
    this.partitionSize = this.state.partition_size;
    this.propertyFilterState = this.controlValuesToFilterState(
      this.state.control_values
    );
  }

  resetSearchState() {
    storeInstance.dispatch("setState", {
      property: "search_results",
      value: [],
    });
    storeInstance.dispatch("setState", {
      property: "is_results_loading",
      value: true,
    });
  }

  updated(changedProperties) {
    if (
      changedProperties.has("controlDefs") &&
      window.location.href.includes(this.pageName)
    ) {
      if (this.controlDefs?.length > 0) {
        this.parseQueryString();
        if (this.currentPage <= this.partitionSize) {
          this.getResults();
        } else {
          this.getAdjacentResults();
        }
      }
    }

    if (changedProperties.has("searchResults")) {
      this.visibleResults = this.searchResults;
      if (this.currentPage <= this.partitionSize) {
        this.cycleResults((this.currentPage - 1) * this.resultsPerPage);
      }
      this.setUpPageNumbers(this.currentPage);
    }
    this.renderRoot.querySelector(".results").scrollTo(0, 0);
  }

  checkIfMorePages(quotient, remainder, currentPage) {
    if (currentPage > this.partitionSize) {
      return (quotient > 0) & (remainder > 0);
    }

    if (currentPage === this.partitionSize) {
      return quotient >= this.partitionSize && remainder > 0;
    }

    if (currentPage < this.partitionSize) {
      return quotient >= currentPage && remainder > 0;
    }
  }

  setUpPageNumbers(currentPage = 1) {
    let start;
    let offset = currentPage - (this.partitionSize - 2);
    let quotient = Math.floor(this.searchResults.length / this.resultsPerPage);
    let remainder = this.searchResults.length % this.resultsPerPage;
    this.hasMorePages = this.checkIfMorePages(quotient, remainder, currentPage);
    if (currentPage >= this.partitionSize) {
      if (this.hasMorePages) {
        this.pageNumbers = range(this.partitionSize, offset);
      } else {
        this.pageNumbers = range(this.partitionSize, offset - 1);
      }
    } else {
      let resultsPages =
        quotient < this.partitionSize
          ? quotient + 1 * Boolean(remainder)
          : this.partitionSize;
      start = Math.max(1, resultsPages - this.partitionSize);
      this.pageNumbers = range(resultsPages, start);
    }
  }

  getPageNumber() {
    let queryParams = new URLSearchParams(window.location.search);
    let pageNumber = queryParams.get("page");
    if (pageNumber && pageNumber !== "1") {
      return Number(queryParams.get("page"));
    } else {
      return 1;
    }
  }

  /*--- Query Strings ---*/

  parseQueryString() {
    this.propertyFilterState = {};
    storeInstance.dispatch("setState", {
      property: "current_results_page",
      value: this.getPageNumber(),
    });
    let queryParams = new URLSearchParams(window.location.search);
    if (Array.from(queryParams).length > 0) {
      for (const [key, value] of queryParams) {
        let controlDefId = this.getIdFromControlDef(key);
        if (controlDefId) {
          this.propertyFilterState[controlDefId] = value;
        }
      }
    }
    let controlValues = this.filterStateToControlValues();
    storeInstance.dispatch("setState", {
      property: "control_values",
      value: { ...controlValues },
    });
  }

  generateQueryString(controlValues) {
    let searchParams = new URLSearchParams();
    Object.keys(controlValues).forEach((controlDefId) => {
      searchParams.set(
        `${controlDefId}`,
        this.lstValuesToString(controlValues[controlDefId])
      );
    });
    return searchParams.toString();
  }

  updateUrl(queryString) {
    if (`?${queryString}` !== window.location.search) {
      if (queryString) {
        let newUrl = `/kb/${this.state.langCode}/${this.pageName}?${queryString}`;
        history.pushState({ ...this.propertyFilterState }, null, newUrl);
      } else {
        let newUrl = `/kb/${this.state.langCode}/${this.pageName}`;
        history.pushState({ ...this.propertyFilterState }, null, newUrl);
      }
    }
  }

  lstValuesToString(lstValues) {
    return lstValues.map((item) => item.id).join(",");
  }

  /*--- Data conversion Fcns ---*/

  filterStateToControlValues() {
    let controlValues = {};
    let controlDefIds = Object.keys(this.propertyFilterState);
    if (Array.isArray(controlDefIds) && controlDefIds.length > 0) {
      controlDefIds.forEach((controlDefID) => {
        controlValues[controlDefID] = this.propertyFilterState[controlDefID]
          .split(",")
          .map((value) => ({ id: value, value: value }));
      });
      return controlValues;
    }
  }

  controlValuesToFilterState(controlValues) {
    let newFilterState = {};
    Object.keys(controlValues).forEach((controlDefId) => {
      let valueIds = controlValues[controlDefId].map((lstValue) => lstValue.id);
      newFilterState[controlDefId] = valueIds.join(",");
    });
    return newFilterState;
  }

  getIdFromControlDef(key) {
    let foundControlDef = this.controlDefs.find(
      (controlDef) => controlDef.id == key
    );
    return foundControlDef?.id;
  }

  /*--- Helpers ---*/

  addPageNumberToParams(searchParams, pageNumber) {
    if (pageNumber > 1) {
      searchParams.set("page", pageNumber);
    } else {
      searchParams.delete("page");
    }
  }

  /*--- Event Handlers ---*/

  handlePageNumberClick(e) {
    let pageNumber = Number(e.target.innerText);
    this.setUpPageNumbers(pageNumber);
    let searchParams = new URLSearchParams(window.location.search);
    this.addPageNumberToParams(searchParams, pageNumber);
    this.cycleResults((pageNumber - 1) * this.resultsPerPage);
    storeInstance.dispatch("setState", {
      property: "current_results_page",
      value: pageNumber,
    });
    this.updateUrl(searchParams.toString());
    if (pageNumber > this.partitionSize) {
      this.getAdjacentResults();
    }

    if (
      pageNumber <= this.partitionSize &&
      this.searchResults.length < this.currentPage * this.resultsPerPage
    ) {
      this.getResults();
    }
    this.renderRoot.querySelector(".results").scrollTo(0, 0);
  }

  handlePrevClick() {
    let pageNumber = this.currentPage > 1 ? this.currentPage - 1 : 1;
    this.setUpPageNumbers(pageNumber);
    let searchParams = new URLSearchParams(window.location.search);
    this.addPageNumberToParams(searchParams, pageNumber);
    this.cycleResults((pageNumber - 1) * this.resultsPerPage);
    storeInstance.dispatch("setState", {
      property: "current_results_page",
      value: pageNumber,
    });
    this.updateUrl(searchParams.toString());
    if (pageNumber > this.partitionSize) {
      this.getAdjacentResults();
    }

    if (
      pageNumber <= this.partitionSize &&
      this.searchResults.length < this.partitionSize * this.resultsPerPage
    ) {
      this.getResults();
    }
    this.renderRoot.querySelector(".results").scrollTo(0, 0);
  }

  handleNextClick() {
    let pageNumber = this.currentPage + 1;
    this.setUpPageNumbers(pageNumber);
    let searchParams = new URLSearchParams(window.location.search);
    searchParams.set("page", pageNumber);
    this.cycleResults((pageNumber - 1) * this.resultsPerPage);
    storeInstance.dispatch("setState", {
      property: "current_results_page",
      value: pageNumber,
    });
    this.updateUrl(searchParams.toString());
    if (pageNumber > this.partitionSize) {
      this.getAdjacentResults();
    }
    this.renderRoot.querySelector(".results").scrollTo(0, 0);
  }

  cycleResults(startRow) {
    this.visibleResults = this.searchResults.slice(
      startRow,
      startRow + this.resultsPerPage
    );
  }

  handleHistoryButtonClick() {
    if (window.location.href.includes(this.pageName)) {
      this.parseQueryString();
      this.getResults();
    }
  }

  handlePropertyFilterClick(e) {
    if (e.detail?.lstValues) {
      let queryString = this.generateQueryString(e.detail.lstValues);
      this.propertyFilterState = this.controlValuesToFilterState(
        e.detail.lstValues
      );
      storeInstance.dispatch("setState", {
        property: "control_values",
        value: { ...e.detail.lstValues },
      });
      storeInstance.dispatch("setState", {
        property: "current_results_page",
        value: 1,
      });
      this.updateUrl(queryString);
      this.getResults();
      this.setUpPageNumbers();
    }
  }

  getResults() {}

  getAdjacentResults() {}

  resultsTemplate() {}

  bodyTemplate() {
    if (this.isLoading) {
      return html`<style>
          .loader {
            margin-top: 2rem;
            opacity: 0;
            animation-name: fadeIn;
            animation-duration: 450ms, 600ms;
            animation-timing-function: linear, ease;
            animation-delay: 400ms;
            animation-fill-mode: forwards;
          }

          @keyframes fadeIn {
            0% {
              opacity: 0;
            }
            100% {
              opacity: 1;
            }
          }
        </style>
        <div class="loader">${this.loadingTemplate()}</div>`;
    }

    if (this.searchResults.length === 0) {
      return html`<p>No search results found</p>`;
    }
    return this.resultsTemplate();
  }

  headerTemplate() {}

  loadingTemplate() {
    return html` <style>
        .empty-item__container {
          padding: 0;
        }
        .empty-item {
          list-style: none;
          margin-bottom: 0.5rem;
          height: 5rem;
        }
        .line {
          opacity: 50%;
          background-color: var(--kb-base-highlight);
          width: 100%;
          margin-bottom: 0.25rem;
        }
        .big-line {
          height: 0.75rem;
          margin-bottom: 0.5rem;
          background-color: var(--kb-primary-color-100);
        }
        .small-line-1 {
          height: 0.25rem;
          width: 75%;
        }

        .small-line-2 {
          height: 0.25rem;
          width: 55%;
        }

        .small-line-3 {
          height: 0.25rem;
          width: 35%;
        }

        .animate {
          animation: shimmer 7s infinite;
          background: linear-gradient(
            to right,
            #eff1f3 4%,
            #e2e2e2 25%,
            #eff1f3 36%
          );
        }

        @keyframes shimmer {
          0% {
            background-position: -1000px 0;
          }
          100% {
            background-position: 1000px 0;
          }
        }
      </style>
      <ul class="empty-item__container">
        ${range(8, 1).map(
          () =>
            html`<li class="empty-item">
              <div class="big-line animate line"></div>
              <div class="small-line-1 animate line"></div>
            </li>`
        )}
      </ul>`;
  }

  leftArrowTemplate() {
    if (this.currentPage && this.currentPage > 1) {
      return html` <span @click=${this.handlePrevClick} class="arrow"
        ><iron-icon icon="vaadin:chevron-left-small"></iron-icon
      ></span>`;
    }
    return null;
  }

  rightArrowTemplate() {
    if (this.hasMorePages) {
      return html`<span class="arrow" @click=${this.handleNextClick}
        ><iron-icon icon="vaadin:chevron-right-small"></iron-icon
      ></span>`;
    }
    return null;
  }

  pageNumbersTemplate() {
    return html`
      ${this.searchResults?.length > 0 && this.hasPaging
        ? html`
            <div class="pagination-container">
              ${this.leftArrowTemplate()}
              <ul class="pagenumber-container">
                ${this.pageNumbers.map(
                  (number) =>
                    html`<li
                      @click=${this.handlePageNumberClick}
                      class="pagenumber"
                      style=${this.currentPage === number
                        ? "background-color: var(--kb-pagenumber-active-color); color: white;border-bottom: 2px solid  var(--kb-pagenumber-active-color);"
                        : ""}
                    >
                      ${number}
                    </li>`
                )}
              </ul>
              ${this.rightArrowTemplate()}
            </div>
          `
        : null}
    `;
  }

  render() {
    return html`
      <div class="main">
        <bds-property-filter
          class="filter"
          @filterClick=${this.handlePropertyFilterClick}
          .controlDefs=${this.controlDefs}
          .controlData=${this.controlData}
          .initialState=${this.propertyFilterState}
        >
        </bds-property-filter>
        <section class=${classMap(this.resultsClasses)}>
          ${this.headerTemplate()} ${this.bodyTemplate()}
          ${this.isLoading ? null : this.pageNumbersTemplate()}
        </section>
      </div>
    `;
  }

  static get styles() {
    return css`
      .result {
        background: var(--kb-publication-result-background);
        list-style: none;
        padding: var(--kb-publication-result-padding);
        border-bottom: 2px solid var(--kb-publication-result-border-color);
        margin: var(--kb-publication-result-margin);
      }

      .arrow:hover {
        cursor: pointer;
      }

      .pagination-container {
        display: flex;
        align-items: center;
        justify-content: center;
        margin-top: 2rem;
      }

      .pagenumber-container {
        display: flex;
        justify-content: center;
        padding: 0;
        margin: 0 1rem;
      }

      .pagenumber {
        padding: var(--kb-pagenumber-padding);
        margin: var(--kb-pagenumber-margin);
        list-style: none;
        background-color: var(--kb-pagenumber-background-color);
        border-bottom: 2px solid var(--kb-pagenumber-background-color);
      }

      .pagenumber:hover {
        cursor: pointer;
        border-bottom: 2px solid var(--kb-pagenumber-active-color);
      }

      .result-content {
        display: flex;
        justify-content: space-between;
        flex-direction: column;
      }

      .badge {
        padding: 0.25rem 0.75rem;
        border-radius: 20px;
        font-size: 0.9375rem;
        display: flex;
        align-items: center;
        gap: 4px;
        height: fit-content;
        align-self: end;
      }

      .shortcut-icon {
        padding: 0.25rem 0;
      }

      .results-caption {
        color: var(--kb-search-caption-color);
        font-size: var(--kb-search-caption-fontsize);
        margin: var(--kb-search-caption-margin);
      }

      .result__name {
        font-weight: var(--kb-publication-result-name-fontweight);
        color: var(--kb-publication-result-name-color);
        font-size: var(--kb-publication-result-name-fontsize);
        margin: var(--kb-publication-result-name-margin);
      }

      .result__description {
        color: var(--kb-publication-result-description-color);
        margin: var(--kb-publication-result-description-margin);
      }

      iron-icon {
        width: 1rem;
        position: inherit;
      }

      .results {
        display: block;
        margin: 2rem 1rem;
        overflow-y: auto;
      }

      .result__details-container {
        display: flex;
        justify-content: flex-end;
        align-items: flex-end;
      }

      .last-modified {
        font-size: 0.875rem;
      }

      .result:hover {
        cursor: pointer;
        background-color: var(--kb-publication-result-highlight);
      }

      .filter {
        background-color: var(--kb-property-filter-background);
        height: calc(100vh - var(--kb-header-height, 6rem));
        overflow: auto;
      }

      @media only screen and (min-width: 640px) {
        .main {
          display: grid;
          grid-template-columns: repeat(12, 1fr);
          gap: 5px;
          height: calc(100vh - var(--kb-header-height, 6rem));
        }

        .result-content {
          flex-direction: row;
        }

        .filter {
          display: block;
          grid-column: 1 / span 3;
        }
        .results {
          grid-column: 5 / span 8;
          padding: 2.5rem 0rem;
          margin: 0;
        }
        .results--empty {
          grid-column: 1 / span 8;
          margin-left: 2.5rem;
        }
      }

      @media only screen and (min-width: 1800px) {
        .filter {
          grid-column: 1 / span 2;
        }

        .results {
          grid-column: 4 / span 8;
          padding: 2rem 0rem;
        }

        .results--empty {
          grid-column: 1 / span 8;
        }
      }

      /* @media only screen and (min-width: 1800px) {
        .results {
          grid-column: 4 / span 8;
          padding: 2rem 0rem;
        }

        .results--empty {
          grid-column: 1 / span 8;
        }
      } */
    `;
  }
}
